import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  AlertCircle,
  TrendingUp,
  FileText,
  Target,
  Users,
  Zap,
  Clock
} from 'lucide-react';

interface Activity {
  id: string;
  type: 'success' | 'warning' | 'info' | 'campaign' | 'content' | 'competitor';
  title: string;
  description?: string;
  timestamp: string;
  link?: string;
}

interface ActivityFeedProps {
  activities?: Activity[];
  maxItems?: number;
  loading?: boolean;
  className?: string;
}

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'success':
      return <Check className="h-4 w-4 text-green-600" />;
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-amber-600" />;
    case 'info':
      return <Zap className="h-4 w-4 text-blue-600" />;
    case 'campaign':
      return <Target className="h-4 w-4 text-purple-600" />;
    case 'content':
      return <FileText className="h-4 w-4 text-pink-600" />;
    case 'competitor':
      return <Users className="h-4 w-4 text-orange-600" />;
    default:
      return <TrendingUp className="h-4 w-4 text-gray-600" />;
  }
};

const getActivityBg = (type: Activity['type']) => {
  switch (type) {
    case 'success':
      return 'bg-green-100 dark:bg-green-900/30';
    case 'warning':
      return 'bg-amber-100 dark:bg-amber-900/30';
    case 'info':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'campaign':
      return 'bg-purple-100 dark:bg-purple-900/30';
    case 'content':
      return 'bg-pink-100 dark:bg-pink-900/30';
    case 'competitor':
      return 'bg-orange-100 dark:bg-orange-900/30';
    default:
      return 'bg-gray-100 dark:bg-gray-700';
  }
};

const formatTimestamp = (timestamp: string, t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('common.now', 'Now');
  if (diffMins < 60) return t('common.minutesAgo', '{{count}}min ago', { count: diffMins });
  if (diffHours < 24) return t('common.hoursAgo', '{{count}}h ago', { count: diffHours });
  if (diffDays < 7) return t('common.daysAgo', '{{count}}d ago', { count: diffDays });

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities = [],
  maxItems = 5,
  loading = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const displayActivities = activities.slice(0, maxItems);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center">
          <Clock className="h-4 w-4 mr-2 text-gray-400" />
          {t('dashboard.recentActivity', 'Recent Activity')}
        </h3>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {loading ? (
          Array.from({ length: maxItems }).map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1">
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                  <div className="h-3 w-1/2 bg-gray-100 dark:bg-gray-600 rounded" />
                </div>
              </div>
            </div>
          ))
        ) : displayActivities.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>{t('dashboard.noActivity', 'No recent activity')}</p>
          </div>
        ) : (
          displayActivities.map((activity) => (
            <div
              key={activity.id}
              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityBg(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {activity.title}
                  </p>
                  {activity.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {activity.description}
                    </p>
                  )}
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                  {formatTimestamp(activity.timestamp, t)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {activities.length > maxItems && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button className="w-full text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
            {t('dashboard.viewAllActivity', 'View all activity')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
